import React from 'react';
import {ClientTable, TableColumn, Button, Icon, Menu, MenuItem} from '@mc/ui-v4';
import {mcDelete} from '@mc/react-ui-icons';
import {TypDokumentuVO} from '../../../api';

export interface DocumentRow<TId extends number | string = number | string> {
    id: TId;
    nazev?: string;
    kodTypDokument?: string;
    popis?: string;
    url?: string;
}

interface DocumentTableProps<TId extends number | string = number | string> {
    documents: DocumentRow<TId>[];
    handleDelete?: (e: React.MouseEvent<HTMLElement, MouseEvent>, id: TId) => void;
    handleUpdate?: (documentId: TId, key: string, value: string) => void;
    getDocumentUrl: (documentId: TId) => string;
    typyDokumentu?: TypDokumentuVO[];
    editable?: boolean;
}

export function DocumentTable<TId extends number | string = number | string>({
    documents,
    typyDokumentu,
    handleDelete,
    handleUpdate,
    getDocumentUrl,
    editable,
}: DocumentTableProps<TId>): React.ReactElement {
    const columns: TableColumn<DocumentRow<TId>>[] = [
        {
            header: 'Název souboru',
            accessor: 'nazev',
            width: 182,
            cell: ({row}) => (
                <a href={row.url ?? getDocumentUrl(row.id)} target="_blank" rel="noopener noreferrer" title={row.nazev}>
                    {row.nazev}
                </a>
            ),
        },
        {
            header: 'Typ',
            accessor: 'kodTypDokument',
            className: 'p-0',
            width: 182,
            cell: ({row}) => (
                <Button
                    style="link"
                    key={String(row.id)}
                    menu={
                        <Menu>
                            {typyDokumentu
                                ? typyDokumentu.map(item => {
                                      return (
                                          <MenuItem
                                              key={item.kod}
                                              text={item.nazev}
                                              onClick={() => {
                                                  handleUpdate?.(row.id, 'kodTypDokument', item.kod);
                                              }}
                                          ></MenuItem>
                                      );
                                  })
                                : null}
                        </Menu>
                    }
                >
                    <span>{typyDokumentu ? typyDokumentu.find(dok => dok.kod === row.kodTypDokument)?.nazev : row.kodTypDokument}</span>
                </Button>
            ),
        },
        {
            header: 'Popis',
            accessor: 'popis',
            className: 'p-0',
            width: 243,
            cell: ({row}) => {
                return <input className="w-full" value={row.popis ?? ''} onChange={e => handleUpdate?.(row.id, 'popis', e.target.value)} />;
            },
        },
        {
            id: 'button',
            width: 30,
            className: 'p-0',
            cell: ({row}) => <Button style={'link'} icon={<Icon icon={mcDelete} />} className={'thumbDelete'} onClick={e => handleDelete?.(e, row.id)} />,
        },
    ];
    const nonEditableColumns: TableColumn<DocumentRow<TId>>[] = [
        {
            header: 'Název souboru',
            accessor: 'nazev',
            width: '25%',
            cell: ({row}) => (
                <a href={row.url ?? getDocumentUrl(row.id)} target="_blank" rel="noopener noreferrer" title={row.nazev}>
                    {row.nazev}
                </a>
            ),
        },
        {
            header: 'Typ',
            accessor: 'kodTypDokument',
            width: '25%',
            cell: ({row}) => <span>{row.kodTypDokument}</span>,
        },
        {
            header: 'Popis',
            accessor: 'popis',
            width: '50%',
            cell: ({row}) => {
                return <span>{row.popis}</span>;
            },
        },
    ];

    return (
        <ClientTable<DocumentRow<TId>>
            multisort
            columns={editable ? columns : nonEditableColumns}
            rows={documents}
            getRowProps={_ => {
                return {
                    onClick: e => {
                        e.stopPropagation();
                    },
                };
            }}
        />
    );
}
